
import React, { useMemo, useRef, useState, useEffect } from "react";
import {
  Document,
  Packer,
  Paragraph,
  HeadingLevel,
  TextRun,
  BulletLevel,
} from "docx";

import { saveAs } from "file-saver";
import {
  uploadVideo,
  uploadLiveVideo,
  getVideoStatus,
  chatWithVideo,
  getVideoPreview,
  getTranscript,
  chatWithIndexedVideo,
  getLibraryVideos,
  chatWithLibrary
} from "./api";
import {
  Search,
  Plus,
  Library,
  LayoutDashboard,
  BarChart3,
  Settings,
  HelpCircle,
  Bell,
  Upload,
  Bot,
  UserRound,
  Paperclip,
  Send,
  MoreVertical,
  Sparkles,
  ShieldCheck,
  Menu,
  X,
  Grid2X2,
  List,
  SlidersHorizontal,
  FileText,
  MessageSquare,
  Hourglass,
  CheckCircle2,
  Languages,
  CloudUpload,
  Video,
  Mic,
  BarChart2,
  Lock,
  Activity,
  Database,
  Save,
  ToggleRight,
  Download,
  Eye,
  XCircle,
  MessageSquareWarning
} from "lucide-react";
import "./App.css";

import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const ASSETS = {
  boardroom: "/boardroom.png",
  avatar: "/avatar.png",

  /**
   * Optional library thumbnails.
   * Put these files in /public if available.
   * If unavailable, CSS fallback thumbnails will still render.
   */
  libraryBoardroom: "/library-boardroom.png",
  libraryAnalytics: "/library-analytics.png",
  libraryMeeting: "/library-meeting.png",
};

const STATUS_MAP = {
  Uploaded: "In Progress",
  Uploading: "In Progress",
  Embedding: "In Progress",
  Ingestion: "In Progress",
  Completed: "Completed",
  Failed: "Failed",
};

const NAV_ITEMS = [
  { id: "library", label: "Library", icon: Library },
  { id: "workspace", label: "Workspace", icon: LayoutDashboard },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
  // { id: "settings", label: "Settings", icon: Settings },
];

const DEFAULT_EMPTY_MESSAGES = [
  {
    id: "empty-m1",
    type: "assistant",
    text:
      "No video is selected yet. Choose a video from the Library or upload a new asset to begin AI transcription and analysis.",
    time: "System",
  },
];

export default function App() {
  const fileInputRef = useRef(null);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeNav, setActiveNav] = useState("library");
  const [search, setSearch] = useState("");
  const [chatValue, setChatValue] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [videos, setVideos] = useState([]);
  const pollingRefs = useRef({});
  const [selectedVideoId, setSelectedVideoId] = useState(null);
  const [librarySelectedVideoId, setLibrarySelectedVideoId] = useState(null);
  const [selectedLibraryTile, setSelectedLibraryTile] = useState(null);
  const [transcript, setTranscript] = useState([]);
  const [showTranscript, setShowTranscript] = useState(false);
  const [loadingTranscript, setLoadingTranscript] = useState(false);
  const [transcriptVideo, setTranscriptVideo] = useState(null);
  const [libraryChatOpen, setLibraryChatOpen] = useState(false);

  const [libraryMessages, setLibraryMessages] = useState([
    {
      id: crypto.randomUUID(),
      type: "assistant",
      text: "Hi! Ask me anything across all indexed videos in your library.",
      time: now(),
    },
  ]);

  const [libraryChatValue, setLibraryChatValue] = useState("");
  const libraryFeedRef = useRef(null);
  const [viewMode, setViewMode] = useState("grid");
  // const [messageMap, setMessageMap] = useState(() =>
  //   VIDEO_ITEMS.reduce((acc, video) => {
  //     acc[video.id] = video.chatMessages;
  //     return acc;
  //   }, {})
  // );

  const [messageMap, setMessageMap] = useState({});

  const selectedVideo = useMemo(
    () =>
      videos.find(
        (video) => String(video.video_id) === String(selectedVideoId)
      ) || null,
    [videos, selectedVideoId]
  );

  const activeLabel = useMemo(() => {
    const matched = NAV_ITEMS.find((item) => item.id === activeNav);
    return matched?.label ?? "Workspace";
  }, [activeNav]);

  const currentMessages = selectedVideo
    ? messageMap[selectedVideo.video_id] || []
    : DEFAULT_EMPTY_MESSAGES;

  // const filteredVideos = useMemo(() => {

  //   const query = search.trim().toLowerCase();

  //   if (!query) return videos;

  //   return videos.filter((video) => {

  //     return [
  //       video.video_title,
  //       video.status,
  //       video.video_size,
  //     ]
  //       .join(" ")
  //       .toLowerCase()
  //       .includes(query);

  //   });

  // }, [videos, search]);

  const filteredVideos = useMemo(() => {

    return videos.filter(v => v.source === "library");

  }, [videos]);

  function now() {
    return new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(new Date());
  }


  useEffect(() => {
    // const socket = new WebSocket("ws://127.0.0.1:8000/ws");
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const socket = new WebSocket(`${proto}//${window.location.host}/ws`);

    socket.onopen = () => {
      console.log("✅ WebSocket Connected");
    };

    socket.onmessage = ({ data }) => {

      const update = JSON.parse(data);

      if (update.type === "ping") return;

      if (update.type !== "video_status") return;

      // setVideos(prev => prev.map(video => {

      //   if (String(video.video_id) !== String(update.video_id))
      //     return video;

      //   return {

      //     ...video,

      //     status: update.status

      //   };

      // }));

      setVideos(prev =>
        prev.map(video =>
          String(video.video_id) === String(update.video_id)
            ? {
              ...video,
              status: update.status
            }
            : video
        )
      );
    };

    socket.onerror = (e) => console.log("WS ERROR", e);

    socket.onclose = (e) => console.log("WS CLOSED", e);

    return () => socket.close();
  }, []);

  useEffect(() => {

    async function loadLibrary() {

      try {

        const res = await getLibraryVideos();

        // const loadedVideos = await Promise.all(

        //   res.videos.map(async (video) => {

        //     let previewUrl = null;

        //     try {

        //       const preview = await getVideoPreview(
        //         video.video_id
        //       );

        //       previewUrl = preview.preview_url;

        //     } catch { }

        //     return {

        //       ...video,

        //       previewUrl,

        //       source: "library"

        //     };

        //   })

        // );

        // setVideos(loadedVideos);

        const loadedVideos = res.videos.map(video => ({
          ...video,
          source: "library",
          previewUrl: null
        }));

        setVideos(loadedVideos);

        loadedVideos.forEach(async (video) => {

          try {

            const preview = await getVideoPreview(video.video_id);

            setVideos(prev =>
              prev.map(v =>
                String(v.video_id) === String(video.video_id)
                  ? {
                    ...v,
                    previewUrl: preview.preview_url
                  }
                  : v
              )
            );

          } catch { }

        });

        // setVideos(loadedVideos);

        // // Fetch previews in background
        // loadedVideos.forEach(async (video) => {
        //   try {
        //     const preview = await getVideoPreview(video.video_id);

        //     setVideos(prev =>
        //       prev.map(v =>
        //         v.video_id === video.video_id
        //           ? { ...v, previewUrl: preview.preview_url }
        //           : v
        //       )
        //     );

        //   } catch { }
        // });

        console.log(res);
        console.log(loadedVideos);

      }

      catch (err) {

        console.error(err);

      }

    }

    loadLibrary();

  }, []);

  useEffect(() => {

    if (!libraryFeedRef.current) return;

    libraryFeedRef.current.scrollTop =
      libraryFeedRef.current.scrollHeight;

  }, [libraryMessages]);

  // function handleNavChange(id) {
  //   setActiveNav(id);
  //   setSidebarOpen(false);
  // }

  function handleNavChange(id) {

    // Leaving Library → clear transcript state
    if (activeNav === "library" && id !== "library") {
      setTranscript([]);
      setTranscriptVideo(null);
      setShowTranscript(false);
    }

    setActiveNav(id);
    setSidebarOpen(false);
  }

  // function handleVideoSelect(video) {
  //   // setSelectedVideoId(video.id);
  //   setSelectedVideoId(video.video_id);
  //   setActiveNav("workspace");
  // }

  async function handleVideoSelect(video) {

    if (!video.previewUrl) {

      try {

        const res = await getVideoPreview(video.video_id);

        setVideos(prev =>
          prev.map(v =>
            String(v.video_id) === String(video.video_id)
              ? {
                ...v,
                previewUrl: res.preview_url
              }
              : v
          )
        );

      } catch { }

    }

    // only remember selected tile
    setSelectedLibraryTile(video);

    // library AI should now target this video if opened
    setLibrarySelectedVideoId(video.video_id);

  }

  // async function handleVideoSelect(video) {

  //   if (!video.previewUrl) {

  //     const res = await getVideoPreview(video.video_id);

  //     // video.previewUrl = res.preview_url;

  //     setVideos(prev =>
  //       prev.map(v =>
  //         // String(v.video_id) === String(video.video_id)
  //         String(v.video_s3_path) === String(video.video_s3_path)
  //           ? {
  //             ...v,
  //             previewUrl: res.preview_url
  //           }
  //           : v
  //       )
  //     );
  //   }

  //   // video.source = "library";

  //   // setSelectedVideoId(video.video_id);
  //   setLibrarySelectedVideoId(video.video_id);
  //   setVideos(prev =>
  //     prev.map(v =>
  //       v.video_id === video.video_id
  //         ? { ...v, source: "library" }
  //         : v
  //     )
  //   );

  //   // setSelectedVideoId(video.video_id);

  //   setLibraryChatOpen(true);
  // }

  async function generateThumbnail(file) {

    return new Promise((resolve) => {

      const video = document.createElement("video");

      video.preload = "metadata";
      video.muted = true;
      video.src = URL.createObjectURL(file);

      video.playsInline = true;

      video.onloadeddata = () => {

        video.currentTime = 1;

      };

      video.onseeked = () => {

        const canvas = document.createElement("canvas");

        // canvas.width = 320;
        // canvas.height = 180;

        canvas.width = 1280;
        canvas.height = 720;

        const ctx = canvas.getContext("2d");

        ctx.drawImage(
          video,
          0,
          0,
          canvas.width,
          canvas.height
        );

        // resolve(canvas.toDataURL("image/jpeg"));
        resolve(
          canvas.toDataURL(
            "image/jpeg",
            0.95
          )
        );

        URL.revokeObjectURL(video.src);

      };

      video.onerror = () => resolve(null);

    });

  }

  async function handleLibraryUpload(file) {

    setSelectedFile(null);
    setSelectedVideoId(null);

    const response = await uploadVideo(file);

    const uploaded = response.data;

    // Show placeholder immediately
    // const tempVideo = {
    //   ...uploaded,
    //   source: "library",
    //   previewUrl: null,
    //   thumbnail: null
    // };

    const tempId = `upload-${Date.now()}`;

    const tempVideo = {
      video_id: tempId,
      video_title: file.name,
      video_size: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
      created_at: "Uploading...",
      status: "Uploading",
      source: "library",
      previewUrl: null,
      thumbnail: null,
      isUploading: true
    };

    setVideos(prev => [tempVideo, ...prev]);

    // setSelectedVideoId(uploaded.video_id);

    // Stay inside library
    setActiveNav("library");

    // Refresh library in background
    const res = await getLibraryVideos();

    const loadedVideos = res.videos.map(video => ({
      ...video,
      source: "library",
      previewUrl: null
    }));

    // setVideos(loadedVideos);
    setVideos(prev => {

      const withoutPlaceholder =
        prev.filter(v => !v.isUploading);

      return [...loadedVideos, ...withoutPlaceholder];

    });

    // Load previews lazily
    loadedVideos.forEach(async (video) => {

      try {

        const preview = await getVideoPreview(video.video_id);

        setVideos(prev =>
          prev.map(v =>
            String(v.video_id) === String(video.video_id)
              ? {
                ...v,
                previewUrl: preview.preview_url
              }
              : v
          )
        );

      } catch { }

    });

    setSelectedFile(null);

  }

  async function handleLiveUpload(file) {

    const response = await uploadLiveVideo(file);

    const uploaded = response.data;

    const thumbnail = await generateThumbnail(file);

    const preview = await getVideoPreview(uploaded.video_id, "live");

    const video = {

      source: "live",

      video_id: uploaded.video_id,

      video_title: uploaded.filename,

      video_s3_path: uploaded.video_s3_path,

      video_size: `${(file.size / 1024 / 1024).toFixed(2)} MB`,

      previewUrl: preview.preview_url,

      uploadedOn: uploaded.created_at,

      created_at: uploaded.created_at,

      thumbnail,

      heroImage: thumbnail,

      status: uploaded.status

    };

    setVideos(prev => [
      video,
      ...prev
    ]);

    setSelectedVideoId(video.video_id);

    setActiveNav("workspace");

    setSelectedFile(null);

  }

  // async function handleFiles(files) {

  //   const file = files?.[0];

  //   if (!file) return;

  //   setSelectedFile(file);

  //   // setActiveNav("workspace");
  //   if (activeNav === "workspace") {

  //     setActiveNav("workspace");

  //   }

  //   try {

  //     // const thumbnail = await generateThumbnail(file);

  //     // const response = await uploadVideo(file);

  //     const response =
  //       activeNav === "library"
  //         ? await uploadVideo(file)
  //         : await uploadLiveVideo(file);

  //     const thumbnail = await generateThumbnail(file);

  //     const uploaded = response.data;

  //     const preview = await getVideoPreview(uploaded.video_id);


  //     const latest = await getVideoStatus(uploaded.video_id);

  //     const video = {

  //       // source: "live",

  //       source: activeNav === "library"
  //         ? "library"
  //         : "live",

  //       video_id: uploaded.video_id,

  //       video_title: uploaded.filename,

  //       video_s3_path: uploaded.video_s3_path,

  //       video_size: `${(file.size / 1024 / 1024).toFixed(2)} MB`,

  //       previewUrl: preview.preview_url,

  //       uploadedOn: uploaded.created_at,

  //       created_at: uploaded.created_at,

  //       thumbnail,

  //       heroImage: thumbnail,

  //       // status:uploaded.status


  //       status: latest.data.status
  //     };

  //     // setVideos((prev) => [

  //     //   video,

  //     //   ...prev,

  //     // ]);

  //     // setSelectedVideoId(video.video_id);

  //     // pollVideo(video.video_id);

  //     if (activeNav === "library") {

  //       const res = await getLibraryVideos();

  //       const loadedVideos = await Promise.all(

  //         res.videos.map(async (video) => {

  //           let previewUrl = null;

  //           try {

  //             const preview = await getVideoPreview(video.video_id);

  //             previewUrl = preview.preview_url;

  //           } catch { }

  //           return {

  //             ...video,

  //             previewUrl,

  //             source: "library"

  //           };

  //         })

  //       );

  //       setVideos(loadedVideos);

  //       // setSelectedVideoId(uploaded.video_id);

  //       // setActiveNav("workspace");

  //     } else {

  //       setVideos(prev => [

  //         video,

  //         ...prev

  //       ]);

  //       setSelectedVideoId(video.video_id);

  //     }
  //   }

  //   catch (e) {

  //     alert(e.message);

  //   }

  // }

  async function handleFiles(files) {

    const file = files?.[0];

    if (!file) return;

    // setSelectedFile(file);
    if (activeNav === "workspace") {

      setSelectedFile(file);

    }

    try {

      if (activeNav === "library") {

        await handleLibraryUpload(file);

      } else {

        await handleLiveUpload(file);

      }

    }

    catch (e) {

      console.error(e);

      alert(e.message);

    }

  }

  function formatDuration(seconds) {
    if (seconds == null) return "";

    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);

    return `${mins}:${secs.toString().padStart(2, "0")}`;
  }

  function getVideoName(path) {
    if (!path) return "";

    const file = path.split("/").pop();

    return file.replace(
      /^[0-9a-fA-F-]{36}_/,
      ""
    );
  }

  async function sendMessage() {

    const clean = chatValue.trim();

    if (!clean || !selectedVideo) return;

    const targetVideoId = selectedVideo.video_id;

    const userMessage = {
      id: crypto.randomUUID(),
      type: "user",
      text: clean,
      time: now(),
    };

    const thinkingId = crypto.randomUUID();

    const thinkingMessage = {
      id: thinkingId,
      type: "thinking",
      text: "Generating response...",
      time: now(),
    };

    setMessageMap((prev) => ({
      ...prev,
      [targetVideoId]: [
        ...(prev[targetVideoId] || []),
        userMessage,
        thinkingMessage,
      ],
    }));

    setChatValue("");

    try {

      // const response = await chatWithVideo(
      //   targetVideoId,
      //   selectedVideo?.video_s3_path,
      //   clean
      // );

      let response;

      if (selectedVideo.source === "library") {

        response = await chatWithIndexedVideo(
          targetVideoId,
          clean
        );

      }
      else {

        response = await chatWithVideo(
          targetVideoId,
          selectedVideo.video_s3_path,
          clean,
          true
        );

      }


      let reply = "";

      if (!response.success) {

        reply = response.message || "No answer found.";

      } else {

        // reply =
        //   typeof response.answer === "string"
        //     ? response.answer
        //     : response.answer.message;

        reply =
          typeof response.answer === "string"
            ? response.answer
            : response.answer.message;

        if (response.reference) {

          reply += `

            ----------------------------------------

            📹 Video: ${response.reference.video_title}

            📂 Source: ${getVideoName(response.reference.video_s3_path)}

            ⏱  Scene:  ${formatDuration(response.reference.startSec)} - ${formatDuration(response.reference.endSec)} mins`;

        }

      }

      const assistantMessage = {

        id: crypto.randomUUID(),

        type: "assistant",

        text: reply,

        time: now(),

      };

      setMessageMap((prev) => ({

        ...prev,

        [targetVideoId]:
          prev[targetVideoId]
            .filter((m) => m.id !== thinkingId)
            .concat(assistantMessage),

      }));

    }

    catch (e) {

      setMessageMap((prev) => ({

        ...prev,

        [targetVideoId]:
          prev[targetVideoId]
            .filter((m) => m.id !== thinkingId)
            .concat({

              id: crypto.randomUUID(),

              type: "assistant",

              text: "Something went wrong while querying the video.",

              time: now(),

            }),

      }));

    }

  }

  async function sendLibraryMessage() {

    const clean = libraryChatValue.trim();

    if (!clean) return;

    const userMessage = {

      id: crypto.randomUUID(),

      type: "user",

      text: clean,

      time: now()

    };

    const thinkingId = crypto.randomUUID();

    setLibraryMessages(prev => [

      ...prev,

      userMessage,

      {

        id: thinkingId,

        type: "thinking",

        text: "",

        time: now()

      }

    ]);

    setLibraryChatValue("");

    try {

      // const response =
      //   await chatWithLibrary(clean);

      const response =
        await chatWithLibrary(
          clean,
          // selectedVideoId
          librarySelectedVideoId
        );

      let answer = "";

      if (!response.success) {

        answer =
          response.message ||
          "No answer found.";

      }

      else {

        answer =
          typeof response.answer === "string"
            ? response.answer
            : response.answer.message;

        if (response.reference) {

          answer += `

----------------------------------------

📹 ${response.reference.video_title}

⏱ ${formatDuration(response.reference.startSec)} - ${formatDuration(response.reference.endSec)}

📂 ${getVideoName(response.reference.video_s3_path)}`;

        }

      }

      setLibraryMessages(prev =>

        prev
          .filter(m => m.id !== thinkingId)
          .concat({

            id: crypto.randomUUID(),

            type: "assistant",

            text: answer,

            time: now()

          })

      );

    }

    catch {

      setLibraryMessages(prev =>

        prev
          .filter(m => m.id !== thinkingId)
          .concat({

            id: crypto.randomUUID(),

            type: "assistant",

            text:
              "Something went wrong.",

            time: now()

          })

      );

    }

  }

  async function previewTranscript(video) {

    if (!video) return;

    setLoadingTranscript(true);

    try {

      const res = await getTranscript(video.video_id);

      const parsed =
        typeof res.message === "string"
          ? JSON.parse(res.message)
          : res;

      setTranscript(parsed.segments || []);

      setTranscriptVideo(video);

      setShowTranscript(true);

    } finally {

      setLoadingTranscript(false);

    }

  }

  // async function downloadTranscript(video) {

  //   if (!video) return;

  //   let segments = transcript;

  //   if (
  //     !segments.length ||
  //     selectedLibraryTile?.video_id !== video.video_id
  //   ) {

  //     const res = await getTranscript(video.video_id);

  //     const parsed =
  //       typeof res.message === "string"
  //         ? JSON.parse(res.message)
  //         : res;

  //     segments = parsed.segments || [];

  //   }

  //   const lines = [];

  //   lines.push(video.video_title);

  //   lines.push("");

  //   // segments.forEach(segment => {

  //   //   lines.push(
  //   //     `${segment.start_time} - ${segment.end_time}`
  //   //   );

  //   //   lines.push(segment.summary);

  //   //   lines.push("");

  //   // });

  //   segments.forEach(segment => {

  //     lines.push(
  //       `${segment.start_time} - ${segment.end_time}`
  //     );

  //     lines.push("");

  //     lines.push("Summary");

  //     lines.push(segment.summary);

  //     lines.push("");

  //     if (segment.scene_description) {

  //       lines.push("Scene");

  //       lines.push(segment.scene_description);

  //       lines.push("");

  //     }

  //     if (segment.dialogue) {

  //       lines.push("Dialogue");

  //       lines.push(segment.dialogue);

  //       lines.push("");

  //     }

  //     if (segment.people?.length) {

  //       lines.push("People");

  //       lines.push(...segment.people);

  //       lines.push("");

  //     }

  //     if (segment.objects?.length) {

  //       lines.push("Objects");

  //       lines.push(...segment.objects);

  //       lines.push("");

  //     }

  //     if (segment.actions?.length) {

  //       lines.push("Actions");

  //       lines.push(...segment.actions);

  //       lines.push("");

  //     }

  //     if (segment.onscreen_text?.length) {

  //       lines.push("On-screen Text");

  //       lines.push(...segment.onscreen_text);

  //       lines.push("");

  //     }

  //     if (segment.key_events?.length) {

  //       lines.push("Key Events");

  //       lines.push(...segment.key_events);

  //       lines.push("");

  //     }

  //     lines.push("--------------------------------");

  //     lines.push("");

  //   });
  //   const blob = new Blob(
  //     [lines.join("\n")],
  //     {
  //       type:
  //         "text/plain;charset=utf-8"
  //     }
  //   );

  //   const url =
  //     URL.createObjectURL(blob);

  //   const a =
  //     document.createElement("a");

  //   a.href = url;

  //   a.download =
  //     `${video.video_title}_Transcript.txt`;

  //   a.click();

  //   URL.revokeObjectURL(url);

  // }

  async function downloadTranscript(video) {

    if (!video) return;

    let segments = transcript;

    if (
      !segments.length ||
      selectedLibraryTile?.video_id !== video.video_id
    ) {

      const res = await getTranscript(video.video_id);

      const parsed =
        typeof res.message === "string"
          ? JSON.parse(res.message)
          : res;

      segments = parsed.segments || [];

    }

    const children = [];

    children.push(

      new Paragraph({

        text: video.video_title,

        heading: HeadingLevel.TITLE

      })

    );

    children.push(

      new Paragraph({

        children: [

          new TextRun({

            text: "AI Generated Transcript",

            bold: true

          })

        ]

      })

    );

    children.push(new Paragraph(""));

    segments.forEach(segment => {

      children.push(

        new Paragraph({

          heading: HeadingLevel.HEADING_1,

          text:

            `${segment.start_time} - ${segment.end_time}`

        })

      );

      children.push(

        new Paragraph({

          heading: HeadingLevel.HEADING_2,

          text: "Summary"

        })

      );

      children.push(

        new Paragraph(segment.summary)

      );

      if (segment.scene_description) {

        children.push(

          new Paragraph({

            heading: HeadingLevel.HEADING_2,

            text: "Scene"

          })

        );

        children.push(

          new Paragraph(

            segment.scene_description

          )

        );

      }

      if (segment.dialogue) {

        children.push(

          new Paragraph({

            heading: HeadingLevel.HEADING_2,

            text: "Dialogue"

          })

        );

        children.push(

          new Paragraph(

            segment.dialogue

          )

        );

      }

      if (segment.people?.length) {

        children.push(

          new Paragraph({

            heading: HeadingLevel.HEADING_2,

            text: "People"

          })

        );

        segment.people.forEach(person => {

          children.push(

            new Paragraph({

              bullet: { level: 0 },

              text: person

            })

          );

        });

      }

      if (segment.objects?.length) {

        children.push(

          new Paragraph({

            heading: HeadingLevel.HEADING_2,

            text: "Objects"

          })

        );

        segment.objects.forEach(object => {

          children.push(

            new Paragraph({

              bullet: { level: 0 },

              text: object

            })

          );

        });

      }

      if (segment.actions?.length) {

        children.push(

          new Paragraph({

            heading: HeadingLevel.HEADING_2,

            text: "Actions"

          })

        );

        segment.actions.forEach(action => {

          children.push(

            new Paragraph({

              bullet: { level: 0 },

              text: action

            })

          );

        });

      }

      if (segment.onscreen_text?.length) {

        children.push(

          new Paragraph({

            heading: HeadingLevel.HEADING_2,

            text: "On-screen Text"

          })

        );

        segment.onscreen_text.forEach(text => {

          children.push(

            new Paragraph({

              bullet: { level: 0 },

              text: text

            })

          );

        });

      }

      if (segment.key_events?.length) {

        children.push(

          new Paragraph({

            heading: HeadingLevel.HEADING_2,

            text: "Key Events"

          })

        );

        segment.key_events.forEach(event => {

          children.push(

            new Paragraph({

              bullet: { level: 0 },

              text: event

            })

          );

        });

      }

      children.push(new Paragraph(""));

    });

    const doc = new Document({

      sections: [

        {

          children

        }

      ]

    });

    const blob = await Packer.toBlob(doc);

    saveAs(

      blob,

      `${video.video_title}_Transcript.docx`

    );

  }

  return (
    <div className="app">
      <Sidebar
        open={sidebarOpen}
        activeNav={activeNav}
        onClose={() => setSidebarOpen(false)}
        onNavChange={handleNavChange}
        onNewAnalysis={() => {
          setSelectedVideoId(null);
          setSelectedFile(null);
          setActiveNav("workspace");
        }}
      />

      <div
        className="mobileScrim"
        data-open={sidebarOpen}
        onClick={() => setSidebarOpen(false)}
      />

      <main className="main">
        <Topbar
          activeNav={activeNav}
          search={search}
          onSearchChange={setSearch}
          onMenu={() => setSidebarOpen(true)}
        />

        <input
          ref={fileInputRef}
          hidden
          type="file"
          accept="video/mp4,video/webm,video/quicktime"
          onChange={(e) => handleFiles(e.target.files)}
        />
        {activeNav === "library" && (
          // <LibraryScreen
          //   videos={filteredVideos}
          //   viewMode={viewMode}
          //   onViewModeChange={setViewMode}
          //   onVideoSelect={handleVideoSelect}
          //   // onUploadClick={openUploadModal}
          //   onUploadClick={() => {
          //     setSelectedVideoId(null);
          //     setSelectedFile(null);
          //     // setActiveNav("workspace");

          //     setTimeout(() => {
          //       fileInputRef.current?.click();
          //     }, 100);
          //   }}
          // />

          <LibraryScreen
            videos={filteredVideos}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            onVideoSelect={handleVideoSelect}

            selectedTile={selectedLibraryTile}
            setSelectedVideoId={setSelectedVideoId}
            setActiveNav={setActiveNav}

            onUploadClick={() => {
              setSelectedVideoId(null);
              setSelectedFile(null);

              setTimeout(() => {
                fileInputRef.current?.click();
              }, 100);
            }}

            libraryChatOpen={libraryChatOpen}
            setLibraryChatOpen={setLibraryChatOpen}

            libraryMessages={libraryMessages}
            libraryChatValue={libraryChatValue}
            setLibraryChatValue={setLibraryChatValue}
            sendLibraryMessage={sendLibraryMessage}
            previewTranscript={previewTranscript}
            downloadTranscript={downloadTranscript}
            libraryFeedRef={libraryFeedRef}
            setLibrarySelectedVideoId={setLibrarySelectedVideoId}
          />
          // <LibraryScreen

          //   videos={filteredVideos}

          //   viewMode={viewMode}

          //   onViewModeChange={setViewMode}

          //   onVideoSelect={handleVideoSelect}

          //   onUploadClick={() => {

          //     setSelectedVideoId(null);

          //     setSelectedFile(null);

          //     setTimeout(() => {

          //       fileInputRef.current?.click();

          //     }, 100);

          //   }}

          //   libraryChatOpen={libraryChatOpen}

          //   setLibraryChatOpen={setLibraryChatOpen}

          //   libraryMessages={libraryMessages}

          //   libraryChatValue={libraryChatValue}

          //   setLibraryChatValue={setLibraryChatValue}

          //   sendLibraryMessage={sendLibraryMessage}

          //   libraryFeedRef={libraryFeedRef}

          // />
        )}

        {activeNav === "workspace" && (
          <WorkspaceScreen
            activeLabel={activeLabel}
            selectedVideo={selectedVideo}
            selectedFile={selectedFile}
            fileInputRef={fileInputRef}
            messages={currentMessages}
            chatValue={chatValue}
            onChatChange={setChatValue}
            onSend={sendMessage}
            onAttach={() => fileInputRef.current?.click()}
            onFiles={handleFiles}
            transcriptVideo={transcriptVideo}
          />
        )}

        {activeNav === "analytics" && <AnalyticsScreen videos={videos} />}

        {/* {activeNav === "settings" && <SettingsScreen />} */}

        {
          showTranscript && (

            <TranscriptModal
              title={
                selectedLibraryTile?.video_title
              }
              transcript={transcript}
              onClose={() =>
                setShowTranscript(false)
              }
            />

          )
        }
      </main>

    </div>

  );
}

function Sidebar({
  open,
  activeNav,
  onNavChange,
  onClose,
  onNewAnalysis,
}) {
  return (
    <aside className="sidebar" data-open={open}>
      <div className="sidebarMobileHeader">
        <Brand />
        <button
          className="iconButton"
          onClick={onClose}
          aria-label="Close navigation"
        >
          <X size={24} />
        </button>
      </div>

      <div className="desktopBrand">
        <Brand />
      </div>

      <button className="newAnalysis" onClick={onNewAnalysis}>
        <Plus size={22} />
        <span>New Analysis</span>
      </button>

      <nav className="nav" aria-label="Primary navigation">
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          const selected = activeNav === item.id;

          return (
            <button
              key={item.id}
              className="navItem"
              data-active={selected}
              onClick={() => onNavChange(item.id)}
            >
              <Icon size={23} />
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>

      <button className="helpCenter">
        <HelpCircle size={25} />
        <span>Help Center</span>
      </button>
    </aside>
  );
}

function Brand() {
  return (
    <div className="brand">
      <div className="brandName">ClipIQ</div>
      <div className="brandSub">ENTERPRISE AI</div>
    </div>
  );
}

// function openUploadModal() {
//   setSelectedVideoId(null);
//   setSelectedFile(null);
//   setActiveNav("workspace");

//   setTimeout(() => {
//     fileInputRef.current?.click();
//   }, 100);
// }

function Topbar({ activeNav, search, onSearchChange, onMenu }) {
  const [avatarFailed, setAvatarFailed] = useState(false);

  const placeholder =
    activeNav === "library"
      ? "Search intelligent transcripts..."
      : "Search workspace...";

  return (
    <header className="topbar">
      <button
        className="menuButton"
        onClick={onMenu}
        aria-label="Open navigation"
      >
        <Menu size={25} />
      </button>

      <div className="searchBox">
        <Search size={25} />
        <input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={placeholder}
        />
      </div>

      <div className="topActions">
        <button className="topHelp">
          <HelpCircle size={24} />
          <span>Help</span>
        </button>

        <button className="notificationButton" aria-label="Notifications">
          <Bell size={24} />
          <i />
        </button>

        <div className="divider" />

        <div className="profile">
          <div className="profileText">
            <strong>Alex Thompson</strong>
            <span>SENIOR ANALYST</span>
          </div>

          <div className="avatar">
            {!avatarFailed && (
              <img
                src={ASSETS.avatar}
                alt=""
                onError={() => setAvatarFailed(true)}
              />
            )}
            <span className="avatarFallback" />
          </div>
        </div>
      </div>
    </header>
  );
}

/* -------------------------------------------------------------------------- */
/* Library                                                                    */
/* -------------------------------------------------------------------------- */

// function LibraryScreen({
//   videos,
//   viewMode,
//   onViewModeChange,
//   onVideoSelect,
//   onUploadClick,
// })
function LibraryScreen({

  videos,
  viewMode,
  onViewModeChange,
  onVideoSelect,

  selectedTile,
  setLibrarySelectedVideoId,
  onUploadClick,

  libraryChatOpen,
  setLibraryChatOpen,

  libraryMessages,
  libraryChatValue,
  setLibraryChatValue,
  sendLibraryMessage,
  previewTranscript,
  downloadTranscript,
  libraryFeedRef

}) {
  return (
    <section className="libraryPage">
      <div className="libraryHeader">
        <div>
          <h1>Video Intelligence Library</h1>
          <p>
            Manage and analyze enterprise video recordings with automated
            transcription.
          </p>
        </div>

        <div className="libraryActions">
          <div className="viewToggle">
            <button
              data-active={viewMode === "grid"}
              onClick={() => onViewModeChange("grid")}
              aria-label="Grid view"
            >
              <Grid2X2 size={18} />
            </button>
            <button
              data-active={viewMode === "list"}
              onClick={() => onViewModeChange("list")}
              aria-label="List view"
            >
              <List size={19} />
            </button>
          </div>

          {/* <button className="filterButton">
            <SlidersHorizontal size={17} />
            <span>Filters</span>
          </button> */}
        </div>
      </div>

      <div className={viewMode === "grid" ? "videoLibraryGrid" : "videoLibraryList"}>
        {videos.map((video) => (
          // <VideoTile
          //   // key={video.id}
          //   key={video.video_id}
          //   video={video}
          //   viewMode={viewMode}
          //   onClick={() => onVideoSelect(video)}
          // />
          <VideoTile
            key={video.video_id}
            video={video}
            viewMode={viewMode}

            selected={
              selectedTile?.video_id === video.video_id
            }

            onClick={() => onVideoSelect(video)}

            onOpenChat={() => {

              // setSelectedVideoId(video.video_id);

              // setActiveNav("workspace");
              setLibrarySelectedVideoId(video.video_id);

              setLibraryChatOpen(true);

            }}

            onPreviewTranscript={() =>
              previewTranscript(video)
            }

            onDownloadTranscript={() =>
              downloadTranscript(video)
            }
          />
        ))}

        <UploadVideoTile onClick={onUploadClick} />
      </div>

      {/* <button className="floatingCreateButton" onClick={onUploadClick}>
        <Plus size={26} />
      </button> */}
      <button

        className="libraryChatButton"

        title="Ask Library AI"

        onClick={() =>

          setLibraryChatOpen(

            true

          )

        }

      >

        <Bot size={30} />

      </button>
      {libraryChatOpen && (

        <LibraryChatDrawer

          messages={libraryMessages}

          value={libraryChatValue}

          onChange={setLibraryChatValue}

          onSend={sendLibraryMessage}

          feedRef={libraryFeedRef}

          // selectedVideo={selectedLibraryTile}
          selectedVideo={selectedTile}

          onClose={() => {

            setLibraryChatOpen(false);
            setLibrarySelectedVideoId(null);

          }}

        />

      )}
    </section>
  );
}

// function VideoTile({ video, viewMode, onClick }) {
function VideoTile({

  video,
  viewMode,

  selected,

  onClick,

  onOpenChat,
  onPreviewTranscript,
  onDownloadTranscript

}) {
  const [thumbFailed, setThumbFailed] = useState(false);
  // const isProcessing = video.status === "processing";
  const isProcessing =
    video.status !== "Completed";

  return (
    <button
      // className="videoTile"
      className={`videoTile ${selected ? "selected" : ""}`}
      data-status={video.status}
      data-view={viewMode}
      onClick={onClick}
    >
      <div className="tileThumb">
        {video.thumbnail && !thumbFailed && (
          <img
            src={video.thumbnail}
            alt=""
            onError={() => setThumbFailed(true)}
          />
        )}

        {(!video.thumbnail || thumbFailed) && (
          <div className="tileThumbFallback" data-status={video.status}>
            {isProcessing ? (
              <div className="processingVisual">
                <span />
                <strong>ANALYZING SPEECH... 65%</strong>
              </div>
            ) : (
              <div className="boardroomMini" />
            )}
          </div>
        )}

        {video.duration && <span className="durationBadge">{video.duration}</span>}
      </div>

      <div className="tileBody">
        <div className="tileStateRow">
          <span className="statusPill" data-status={video.status}>
            {/* {video.statusLabel}
             */}
            {STATUS_MAP[video.status]}
          </span>
          <span className="tileDate">{video.created_at}</span>
        </div>

        <h2>{video.video_title}</h2>
        <p>Uploaded on {video.created_at || "Just now"}</p>

        <div className="tileFooter">
          <span>
            {video.status === "processing" ? (
              <Hourglass size={15} />
            ) : video.status === "transcribed" ? (
              <CheckCircle2 size={15} />
            ) : (
              <FileText size={15} />
            )}
            {video.status}
          </span>

          {video.video_size && (
            <span>
              {video.status === "transcribed" ? (
                <Languages size={15} />
              ) : (
                <MessageSquare size={15} />
              )}
              {video.video_size}
            </span>
          )}
        </div>
      </div>
      {
        selected && (

          <div
            className="tileActions"
            onClick={e => e.stopPropagation()}
          >

            <button onClick={onOpenChat}>
              <Bot size={16} />
              Open AI Chat
            </button>

            <button onClick={onPreviewTranscript}>
              <Eye size={16} />
              Preview Transcript
            </button>

            <button onClick={onDownloadTranscript}>
              <Download size={16} />
              Download Transcript
            </button>

          </div>

        )
      }
    </button>
  );
}

function UploadVideoTile({ onClick }) {
  return (
    <button className="uploadVideoTile" onClick={onClick}>
      <div className="uploadVideoIcon">
        <CloudUpload size={28} />
      </div>
      <h2>Upload New Video</h2>
      <p>
        Drag and drop MP4, MOV, or
        <br />
        WEBM files to start AI
        <br />
        transcription
      </p>
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/* Workspace                                                                  */
/* -------------------------------------------------------------------------- */

function WorkspaceScreen({
  activeLabel,
  selectedVideo,
  selectedFile,
  fileInputRef,
  messages,
  chatValue,
  onChatChange,
  onSend,
  onAttach,
  onFiles,
  transcriptVideo
}) {
  if (!selectedVideo && !selectedFile) {
    return (
      <EmptyWorkspace
        inputRef={fileInputRef}
        onFiles={onFiles}
      />
    );
  }

  return (
    <section className="workspace">
      <div className="workspaceInner">
        <section className="contentStack" aria-label={`${activeLabel} content`}>
          <VideoPreview selectedVideo={selectedVideo} selectedFile={selectedFile} />

          <div className="cardGrid">
            {/* <UploadCard
              selectedFile={selectedFile}
              inputRef={fileInputRef}
              onFiles={onFiles}
            /> */}

            <AssetDetails transcriptVideo={transcriptVideo} selectedVideo={selectedVideo} selectedFile={selectedFile} />
          </div>
        </section>

        <ChatPanel
          messages={messages}
          value={chatValue}
          onChange={onChatChange}
          onSend={onSend}
          onAttach={onAttach}
          disabled={!selectedVideo && !selectedFile}
        />
      </div>
    </section>
  );
}

function EmptyWorkspace({ inputRef, onFiles }) {
  function handleDrop(e) {
    e.preventDefault();
    onFiles(e.dataTransfer.files);
  }

  return (
    <section className="emptyWorkspacePage">
      <div className="emptyGridPattern" />

      <div className="emptyWorkspaceInner">
        <div className="emptyIntro">
          <span>INTELLIGENCE ENGINE</span>
          <h1>
            Start New
            <br />
            Intelligence
            <br />
            Session
          </h1>
          <p>
            Transform recorded meetings, surveillance footage
            and operational videos into searchable knowledge
            using ClipIQ's proprietary AI models.
          </p>

          <ul>
            <li>
              <CheckCircle2 size={18} />
              Scene Detection
            </li>
            <li>
              <CheckCircle2 size={18} />
              Context-aware Question Answering
            </li>
            <li>
              <CheckCircle2 size={18} />
              Secure Enterprise Processing
            </li>
          </ul>
        </div>

        <div
          className="emptyUploadPanel"
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
        >
          <div className="emptyUploadIcon">
            <CloudUpload size={48} />
          </div>

          <h2>Upload Asset</h2>
          <p>Drag and drop your file here, or click to browse local storage.</p>

          <button
            className="emptyUploadButton"
            onClick={(e) => {
              e.stopPropagation();
              inputRef.current?.click();
            }}
          >
            <Plus size={22} />
            Upload New Asset
          </button>

          <span>Supported: MP4, MOV, WAV <b>(Max 30 MB)</b></span>

          <input
            ref={inputRef}
            type="file"
            hidden
            accept="video/mp4,video/quicktime,video/webm,audio/mpeg,audio/wav"
            onChange={(e) => onFiles(e.target.files)}
          />
        </div>
      </div>

      <div className="emptyStatus">
        <div>
          <span>STATUS</span>
          <strong>System Ready</strong>
        </div>
        <div>
          <span>QUEUE</span>
          <strong>Low Latency</strong>
        </div>
      </div>
    </section>
  );
}

function VideoPreview({ selectedVideo, selectedFile }) {
  const [imageFailed, setImageFailed] = useState(false);


  return (
    <section className="videoCard">

      {selectedVideo?.previewUrl ? (

        <video
          className="videoImage"
          src={selectedVideo.previewUrl}
          poster={selectedVideo.thumbnail}
          controls
        />

      ) : (

        <BoardroomFallback />

      )}

      <div className="videoShade" />
      <div className="videoBrand">VideoIntel AI</div>

      {selectedFile && !selectedVideo && (
        <div className="uploadProcessingOverlay">
          <CloudUpload size={30} />
          <strong>{selectedFile.name}</strong>
          <span>Upload received. Preparing AI indexing...</span>
        </div>
      )}
    </section>
  );
}

function BoardroomFallback() {
  return (
    <div className="fallbackScene">
      <div className="light light1" />
      <div className="light light2" />
      <div className="light light3" />
      <div className="light light4" />

      <div className="windows">
        <span />
        <span />
        <span />
        <span />
        <span />
      </div>

      <div className="skyline">
        {Array.from({ length: 25 }).map((_, index) => (
          <i
            key={index}
            style={{ height: `${42 + ((index * 17) % 88)}px` }}
          />
        ))}
      </div>

      <div className="meetingTable" />
      <div className="chair chairLeft" />
      <div className="chair chairRight" />

      <div className="person person1" />
      <div className="person person2" />
      <div className="person person3" />
      <div className="person person4" />
      <div className="person person5" />
      <div className="person person6" />
    </div>
  );
}

function UploadCard({ selectedFile, inputRef, onFiles }) {
  function handleDrop(e) {
    e.preventDefault();
    onFiles(e.dataTransfer.files);
  }

  return (
    <section
      className="uploadCard"
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
    >
      <div className="uploadIcon">
        <Upload size={25} />
      </div>

      <h2>Upload New Asset</h2>

      <p>
        Drag and drop raw footage or
        <br />
        research recordings to begin
        <br />
        AI synthesis.
      </p>

      {selectedFile && (
        <div className="fileName" title={selectedFile.name}>
          {selectedFile.name}
        </div>
      )}

      <button className="selectFiles" onClick={() => inputRef.current?.click()}>
        Select Files
      </button>

      <input
        ref={inputRef}
        type="file"
        hidden
        onChange={(e) => onFiles(e.target.files)}
      />
    </section>
  );
}




function AssetDetails({ transcriptVideo, selectedVideo, selectedFile }) {

  // const [transcript, setTranscript] = useState([]);
  // const [loadingTranscript, setLoadingTranscript] = useState(false);
  // const [showTranscript, setShowTranscript] = useState(false);

  // const title =
  //   selectedVideo?.video_title ||
  //   selectedFile?.name ||
  //   "Uploaded Asset";

  // const title = transcriptVideo?.video_title
  const title =
    transcriptVideo?.video_title ||
    selectedVideo?.video_title ||
    selectedFile?.name ||
    "Uploaded Asset";

  const uploadedOn =
    selectedVideo?.created_at ||
    selectedVideo?.uploadedOn ||
    "Just now";

  const size =
    selectedVideo?.video_size ||
    "Pending";

  const STATUS_DISPLAY = {

    Uploading: {
      title: "Uploading",
      subtitle: "Uploading to S3"
    },

    Uploaded: {
      title: "Uploaded",
      subtitle: "Waiting for analysis"
    },

    Embedding: {
      title: "Embedding",
      subtitle: "Generating embeddings"
    },

    Indexing: {
      title: "Indexing",
      subtitle: "Building search index"
    },

    Analyzing: {
      title: "Analyzing",
      subtitle: "AI is processing the video"
    },

    Completed: {
      title: "Ready",
      subtitle: "Chat Enabled"
    },

    Failed: {
      title: "Failed",
      subtitle: "Processing Failed"
    }

  };

  const status = selectedVideo?.status ?? "Uploaded";

  const ui =
    STATUS_DISPLAY[status] ||
    {
      title: status,
      subtitle: "Processing..."
    };


  // async function openTranscript() {

  //   setLoadingTranscript(true);

  //   try {

  //     const res = await getTranscript(selectedVideo.video_id);

  //     const parsed =
  //       typeof res.message === "string"
  //         ? JSON.parse(res.message)
  //         : res;

  //     setTranscript(parsed.segments || []);

  //     setShowTranscript(true);

  //   }
  //   finally {

  //     setLoadingTranscript(false);

  //   }

  // }

  // function downloadTranscript() {

  //   const lines = [];

  //   lines.push(title);
  //   lines.push("");
  //   lines.push("Transcript");
  //   lines.push("");
  //   transcript.forEach(segment => {

  //     lines.push(
  //       `${segment.start_time} - ${segment.end_time}`)

  //     lines.push(segment.summary)

  //     lines.push("")
  //   })

  //   const blob = new Blob(

  //     [lines.join("\n")],

  //     {
  //       type: "text/plain;charset=utf-8"
  //     }

  //   );

  //   const url = URL.createObjectURL(blob);

  //   const a = document.createElement("a");

  //   a.href = url;

  //   a.download = `${title}_Transcript.txt`;

  //   a.click();

  //   URL.revokeObjectURL(url);

  // }

  return (

    <>

      <section className="assetCard">

        <div className="assetTitleRow">

          <h1>{splitTitle(title)}</h1>

          <span className="secured">

            <ShieldCheck size={14} />

            SECURED

          </span>

        </div>

        <p className="assetMeta">

          Uploaded on {uploadedOn}

          <br />

          {size}

        </p>

        <div className="metricBox">

          <span>

            Processing Status

          </span>

          <strong className="indexing">

            <i />

            {ui.title}
            {/* | {ui.subtitle} */}

          </strong>

        </div>
        {/* 
        {selectedVideo?.source === "library" && (
          <div className="transcriptCard">

            <div className="transcriptHeader">

              <div>

                <h3>Video Transcript</h3>

                <p>Preview automatically generated transcript</p>

              </div>

            </div>

            <div className="transcriptPreview">

              {
                transcript.length
                  ?

                  transcript.slice(0, 4).map(segment => (

                    <div
                      key={segment.start_time}
                      className="transcriptLine"
                    >

                      <span>

                        {segment.start_time}

                      </span>

                      <p>

                        {segment.summary}

                      </p>

                    </div>

                  ))

                  :

                  <p className="emptyTranscript">

                    Click Preview to generate transcript.

                  </p>

              }

            </div>

            <div className="transcriptActions">

              <button

                className="previewBtn"

                onClick={openTranscript}

              >

                <Eye size={16} />

                Preview

              </button>

              <button

                className="downloadBtn"

                onClick={downloadTranscript}

              >

                <Download size={16} />

                Download

              </button>

            </div>

          </div>
        )} */}

      </section>

      {

        // showTranscript &&

        // <TranscriptModal

        //   title={title}
        //   transcript={transcript}
        //   onClose={() => setShowTranscript(false)}

        // />

      }

    </>

  );

}

// function TranscriptModal({ title, transcript, onClose }) {

//   return (

//     <div className="transcriptModalOverlay">

//       <div className="transcriptModal">

//         <div className="modalHeader">

//           <div>

//             <h2>{title}</h2>

//             <span>Transcript Preview</span>

//           </div>

//           <button onClick={onClose}>

//             <XCircle size={22} />

//           </button>

//         </div>

//         <div className="modalBody">

//           {

//             transcript.map(segment => (

//               <div

//                 key={segment.time}

//                 className="modalTranscriptLine"

//               >
//                 <strong>

//                   {segment.start_time}

//                   -

//                   {segment.end_time}

//                 </strong>

//                 <p>

//                   {segment.summary}

//                 </p>

//               </div>

//             ))

//           }

//         </div>

//       </div>

//     </div>

//   );

// }

function TranscriptModal({

  title,

  transcript,

  onClose

}) {

  return (

    <div className="transcriptModalOverlay">

      <div className="transcriptModal">

        <div className="modalHeader">

          <div>

            <h2>{title}</h2>

            <span>

              AI Generated Transcript

            </span>

          </div>

          <button onClick={onClose}>

            <XCircle size={22} />

          </button>

        </div>

        <div className="modalBody">

          {

            transcript.map((segment, index) => (

              <div

                key={index}

                className="modalTranscriptCard"

              >

                <div className="modalTime">

                  {segment.start_time}

                  -

                  {segment.end_time}

                </div>

                <div className="modalSummary">

                  <h4>Summary</h4>

                  <p>

                    {segment.summary}

                  </p>

                </div>

                {

                  segment.scene_description &&

                  <Section

                    title="Scene"

                    value={segment.scene_description}

                  />

                }

                {

                  segment.dialogue &&

                  <Section

                    title="Dialogue"

                    value={segment.dialogue}

                  />

                }

                {

                  segment.people?.length > 0 &&

                  <ListSection

                    title="People"

                    items={segment.people}

                  />

                }

                {

                  segment.objects?.length > 0 &&

                  <ListSection

                    title="Objects"

                    items={segment.objects}

                  />

                }

                {

                  segment.actions?.length > 0 &&

                  <ListSection

                    title="Actions"

                    items={segment.actions}

                  />

                }

                {

                  segment.onscreen_text?.length > 0 &&

                  <ListSection

                    title="On-screen Text"

                    items={segment.onscreen_text}

                  />

                }

                {

                  segment.key_events?.length > 0 &&

                  <ListSection

                    title="Key Events"

                    items={segment.key_events}

                  />

                }

              </div>

            ))

          }

        </div>

      </div>

    </div>

  );

}

function Section({

  title,

  value

}) {

  return (

    <div className="transcriptSection">

      <strong>

        {title}

      </strong>

      <p>

        {value}

      </p>

    </div>

  );

}
function ListSection({

  title,

  items

}) {

  return (

    <div className="transcriptSection">

      <strong>

        {title}

      </strong>

      <ul>

        {

          items.map((item, index) =>

            <li key={index}>

              {item}

            </li>

          )

        }

      </ul>

    </div>

  );

}

function splitTitle(title) {
  const parts = title.split(" ");

  if (parts.length <= 2) return title;

  const midpoint = Math.ceil(parts.length / 2);

  return (
    <>
      {parts.slice(0, midpoint).join(" ")}
      <br />
      {parts.slice(midpoint).join(" ")}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Chat                                                                       */
/* -------------------------------------------------------------------------- */

function ChatPanel({
  messages,
  value,
  onChange,
  onSend,
  onAttach,
  disabled,
}) {
  return (
    <aside className="chatPanel">
      <header className="chatHeader">
        <div className="chatTitle">
          <Sparkles size={28} />
          <h2>Chat with Video</h2>
        </div>

        <button className="moreButton" aria-label="More options">
          <MoreVertical size={26} />
        </button>
      </header>

      <div className="chatFeed">
        {messages.map((message) => (
          <ChatMessage key={message.id} message={message} />
        ))}
      </div>

      <footer className="composer">
        <button className="attachButton" onClick={onAttach} aria-label="Attach file">
          <Paperclip size={26} />
        </button>

        <input
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onSend();
          }}
          placeholder={
            disabled ? "Select a video to start analysis..." : "Ask AI about this video..."
          }
        />

        <button
          className="sendButton"
          onClick={onSend}
          aria-label="Send message"
          disabled={disabled}
        >
          <Send size={28} />
        </button>
      </footer>
    </aside>
  );
}

function ChatMessage({ message }) {
  // const isAssistant = message.type === "assistant";
  const isAssistant =
    message.type === "assistant";

  const isThinking =
    message.type === "thinking";

  return (
    <article className="message" data-type={message.type}>
      <div className="messageRow">
        {/* {isAssistant && ( */}
        {(isAssistant || isThinking) && (
          <div className="messageIcon">
            <Bot size={16} />
          </div>
        )}

        <div className="messageBody">
          <div className="bubble">
            {/* <p>{message.text}</p> */}

            {

              isThinking ?

                <div className="thinkingBubble">

                  <div className="thinkingLoader">

                    <span />

                    <span />

                    <span />

                  </div>

                </div>

                :

                <p>{message.text}</p>

            }

            {message.table && (
              <table className="forecastTable">
                <thead>
                  <tr>
                    <th>MARKET</th>
                    <th>GROWTH</th>
                    <th>TARGET</th>
                  </tr>
                </thead>

                <tbody>
                  {message.table.map((row) => (
                    <tr key={row.market}>
                      <td>{row.market}</td>
                      <td className="growth">{row.growth}</td>
                      <td>{row.target}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="meta">
            {/* {isAssistant ? "AI ASSISTANT" : "ALEX THOMPSON"} • {message.time} */}
            {(isAssistant || isThinking)
              ? "ClipIQ AI"
              : "ALEX THOMPSON"} • {message.time}
          </div>
        </div>

        {!isAssistant && (
          <div className="userIcon">
            <UserRound size={15} />
          </div>
        )}
      </div>
    </article>
  );
}


function LibraryChatDrawer({

  messages,

  value,

  onChange,

  onSend,

  onClose,

  feedRef,

  selectedVideo

}) {

  return (

    <div className="libraryDrawer">

      <div className="libraryDrawerHeader">

        <div className="libraryDrawerTitle">

          <Bot size={22} />

          <div>

            {/* <strong>Library AI</strong>

            <span>

              Search across every indexed video

            </span> */}
            <strong>Library AI</strong>

            <span>

              {selectedVideo
                ? `Searching: ${selectedVideo.video_title}`
                : "Searching across all indexed videos"}

            </span>

          </div>

        </div>

        <button

          onClick={onClose}

          className="drawerClose"

        >

          <X size={20} />

        </button>

      </div>

      <div

        ref={feedRef}

        className="libraryDrawerFeed"

      >

        {

          messages.map(message =>

            <LibraryChatMessage

              key={message.id}

              message={message}

            />

          )

        }

      </div>

      <div className="libraryDrawerComposer">

        <input

          value={value}

          onChange={

            e => onChange(

              e.target.value

            )

          }

          // placeholder="Ask anything across your video library..."
          placeholder="Ask anything. AI will search every indexed video."

          onKeyDown={e => {

            if (e.key === "Enter")

              onSend();

          }}

        />

        <button

          onClick={onSend}

        >

          <Send size={18} />

        </button>

      </div>

    </div>

  );

}

function LibraryChatMessage({

  message

}) {

  const isThinking =

    message.type === "thinking";

  const isAssistant =

    message.type === "assistant";

  return (

    <div

      className={`libraryMessage ${message.type}`}

    >

      <div className="libraryAvatar">

        {

          isAssistant || isThinking

            ?

            <Bot size={16} />

            :

            <UserRound size={16} />

        }

      </div>

      <div className="libraryBubble">

        {

          isThinking

            ?

            <div className="thinkingBubble">

              <div className="thinkingLoader">

                <span />

                <span />

                <span />

              </div>

            </div>

            :

            <LibraryAnswer

              text={message.text}

            />

        }

        <div className="libraryTime">

          {

            isAssistant ||

              isThinking

              ?

              "ClipIQ AI"

              :

              "You"

          }

          •

          {message.time}

        </div>

      </div>

    </div>

  );

}

function LibraryAnswer({ text }) {

  return (
    <p style={{ whiteSpace: "pre-wrap" }}>
      {text}
    </p>
  );

}

/* -------------------------------------------------------------------------- */
/* Analytics / Settings                                                       */
/* -------------------------------------------------------------------------- */

function AnalyticsScreen({ videos }) {

  const completedCount =
    videos.filter(
      v => v.status === "Completed"
    ).length;

  const processingCount =
    videos.filter(
      v =>
        ["Uploaded", "Embedding", "Indexing"]
          .includes(v.status)
    ).length;

  const failedCount =
    videos.filter(
      v => v.status === "Failed"
    ).length;

  return (
    <section className="utilityPage">
      <div className="utilityHeader">
        <span>ENTERPRISE INSIGHTS</span>
        <h1>Analytics</h1>
        <p>
          Operational overview of transcription throughput, indexed video assets,
          and AI processing health.
        </p>
      </div>

      <div className="analyticsGrid">
        <MetricCard
          icon={Database}
          label="Total Assets"
          // value={videos.length}
          value={videos.filter(v => v.source === "library").length}
          caption="Available in library"
        />
        <MetricCard
          icon={CheckCircle2}
          label="Completed"
          value={completedCount}
          caption="Indexed and searchable"
        />
        <MetricCard
          icon={Activity}
          label="Processing"
          value={processingCount}
          caption="Currently in pipeline"
        />
        {/* <MetricCard
          icon={Languages}
          label="Transcribed"
          value={transcribedCount}
          caption="Transcript complete"
        /> */}
        <MetricCard
          icon={MessageSquareWarning}
          label="Failed"
          value={failedCount}
          caption="Failure Occured"
        />
      </div>

      <div className="enterprisePanel">
        <div>
          <BarChart2 size={22} />
          <h2>Processing Distribution</h2>
        </div>

        <div className="bars">
          <span style={{ width: "72%" }}>Transcript Quality</span>
          <span style={{ width: "58%" }}>Sentiment Coverage</span>
          <span style={{ width: "81%" }}>Metadata Extraction</span>
        </div>
      </div>
    </section>
  );
}

function MetricCard({ icon: Icon, label, value, caption }) {
  return (
    <div className="metricCard">
      <Icon size={22} />
      <span>{label}</span>
      <strong>{value}</strong>
      <p>{caption}</p>
    </div>
  );
}

function SettingsScreen() {
  return (
    <section className="utilityPage">
      <div className="utilityHeader">
        <span>ADMINISTRATION</span>
        <h1>Settings</h1>
        <p>
          Configure enterprise workspace preferences, security defaults, and AI
          processing behavior.
        </p>
      </div>

      <div className="settingsGrid">
        <SettingsPanel
          icon={Lock}
          title="Security"
          rows={[
            ["End-to-end encryption", "Enabled"],
            ["Transcript access control", "Role based"],
            ["Audit logging", "Active"],
          ]}
        />

        <SettingsPanel
          icon={Sparkles}
          title="AI Processing"
          rows={[
            ["Auto transcription", "Enabled"],
            ["Sentiment analysis", "Enabled"],
            ["Executive summaries", "Enabled"],
          ]}
        />

        <SettingsPanel
          icon={Save}
          title="Storage"
          rows={[
            ["Retention policy", "365 days"],
            ["Max upload size", "5 GB"],
            ["Archive mode", "Manual"],
          ]}
        />
      </div>
    </section>
  );
}

function SettingsPanel({ icon: Icon, title, rows }) {
  return (
    <div className="settingsPanel">
      <div className="settingsPanelHeader">
        <Icon size={21} />
        <h2>{title}</h2>
      </div>

      {rows.map(([label, value]) => (
        <div className="settingsRow" key={label}>
          <span>{label}</span>
          <strong>
            {value}
            <ToggleRight size={20} />
          </strong>
        </div>
      ))}
    </div>
  );
}